import { useState } from "react";
import PageLayout from "@/components/feature/PageLayout";
import Seo from "@/components/feature/Seo";
import { faqJsonLd, breadcrumbJsonLd } from "@/lib/seo";
import { pageMeta } from "@/lib/pageMeta";
import { faqItems } from "@/mocks/faq";
import { SITE, whatsappLink } from "@/lib/site";

const jsonLd = [
  faqJsonLd,
  breadcrumbJsonLd([
    { name: "Home", path: "/" },
    { name: "FAQ", path: "/faq" },
  ]),
];

export default function Faq() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <PageLayout
      eyebrow="FAQ"
      title={
        <>
          Frequently Asked
          <br />
          <span className="italic text-foreground-500 font-normal">Questions</span>
        </>
      }
      intro="Everything you need to know about booking a private driver in Los Angeles: airport pickups, pricing, service areas, child seats, and custom tours."
    >
      <Seo
        title={pageMeta.faq.title}
        description={pageMeta.faq.description}
        path={pageMeta.faq.path}
        jsonLd={jsonLd}
      />
      <section className="px-4 md:px-6 lg:px-10 pb-14 md:pb-20">
        <div className="max-w-3xl mx-auto space-y-2">
          {faqItems.map((item, idx) => (
            <div
              key={item.id}
              className="bg-background-50 rounded-xl border border-background-200/60 overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
                className="w-full flex items-center justify-between px-4 md:px-5 py-3.5 md:py-4 text-left"
                aria-expanded={openIndex === idx}
              >
                <h2 className="text-foreground-950 text-sm md:text-base font-medium pr-4">
                  {item.question}
                </h2>
                <div className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                  <i className={`ri-${openIndex === idx ? "subtract" : "add"}-line text-sm`} />
                </div>
              </button>
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === idx ? "max-h-96" : "max-h-0"
                }`}
              >
                <p className="px-4 md:px-5 pb-4 md:pb-5 text-foreground-600 text-sm leading-relaxed">
                  {item.answer}
                </p>
              </div>
            </div>
          ))}
        </div>
        <div className="max-w-3xl mx-auto mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
          <a
            href={`tel:${SITE.phone}`}
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-phone-fill" />
            <span>{SITE.phoneDisplay}</span>
          </a>
          <a
            href={whatsappLink("Hi! I have a question about your LA driver service.")}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full border border-background-200/70 text-foreground-800 text-sm font-medium hover:border-primary-400 hover:text-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-whatsapp-fill text-primary-500" />
            <span>Ask on WhatsApp</span>
          </a>
        </div>
      </section>
    </PageLayout>
  );
}
